import { QueryParameterBag } from '@smithy/types'

import {
  DroidMapMetadata,
  ServiceClient,
  ServiceClientContext,
  queryParameterBagFromOffsetPaginationControls,
} from '@droidmap/shared-service-contract'

import {
  CreateGeometryInput,
  DeleteGeometryInput,
  Geometry,
  GetGeometryInput,
  ListGeometriesInput,
  ListGeometriesResult,
  UpdateGeometryInput,
} from '../models/geometry.js'

export class GeometryServiceClient extends ServiceClient {
  public static readonly SERVICE_NAME = 'GeometryService'

  constructor(input: ServiceClientContext) {
    super({ ...input, serviceName: GeometryServiceClient.SERVICE_NAME })
  }

  createGeometry = async (
    input: CreateGeometryInput,
    metadata?: DroidMapMetadata,
  ): Promise<Geometry> => {
    return await this.callApiRouteWithToken<CreateGeometryInput, Geometry>({
      method: 'POST',
      path: '/geometries',
      body: input,
      metadata,
    })
  }

  getGeometry = async (
    input: GetGeometryInput,
    metadata?: DroidMapMetadata,
  ): Promise<Geometry> => {
    return await this.callApiRouteWithToken<string, Geometry>({
      method: 'GET',
      path: `/geometries/${input.geometryId}`,
      metadata,
    })
  }

  updateGeometry = async (
    input: UpdateGeometryInput,
    metadata?: DroidMapMetadata,
  ): Promise<Geometry> => {
    // Body type allows `style`/`properties` to be `null`: a `null` is sent on
    // the wire (JSON `null`) to CLEAR the field, while an omitted (`undefined`)
    // field is dropped by JSON.stringify and leaves the stored value unchanged.
    return await this.callApiRouteWithToken<
      Omit<UpdateGeometryInput, 'geometryId'>,
      Geometry
    >({
      method: 'PATCH',
      path: `/geometries/${input.geometryId}`,
      body: {
        name: input.name,
        geometry: input.geometry,
        style: input.style,
        properties: input.properties,
      },
      metadata,
    })
  }

  deleteGeometry = async (
    input: DeleteGeometryInput,
    metadata?: DroidMapMetadata,
  ): Promise<Geometry> => {
    return await this.callApiRouteWithToken<string, Geometry>({
      method: 'DELETE',
      path: `/geometries/${input.geometryId}`,
      metadata,
    })
  }

  listGeometries = async (
    input: ListGeometriesInput,
    metadata?: DroidMapMetadata,
  ): Promise<ListGeometriesResult> => {
    const {
      projectId,
      assetSetId,
      kind,
      measurementType,
      bbox,
      page,
      pageSize,
      filter,
      sortBy,
      sortDir,
    } = input

    const queryParameters: QueryParameterBag = {
      ...queryParameterBagFromOffsetPaginationControls({
        page,
        pageSize,
        filter,
        sortBy,
        sortDir,
      }),
      projectId,
      ...(assetSetId ? { assetSetId } : {}),
      ...(kind ? { kind } : {}),
      ...(measurementType ? { measurementType } : {}),
      // Serialized as `bbox=minLng,minLat,maxLng,maxLat`.
      ...(bbox ? { bbox: bbox.join(',') } : {}),
    }

    return await this.callApiRouteWithToken<string, ListGeometriesResult>({
      method: 'GET',
      path: '/geometries',
      queryParameters,
      metadata,
    })
  }
}
